import React, { useMemo, useState } from 'react'
import { Box, Flex, Spinner, Text } from '@chakra-ui/core'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import BaseModal from 'components/CommonComponents/BaseModal'
import colors from 'constants/colors'
import LocaleText from 'components/LocaleText'
import { isValidNumberString } from 'utils'

interface IProps {
  isOpen: boolean
  onClose: () => void
}

// ── Color helpers (mirrored from RankingCard.tsx) ─────────────────────────────

const BASE_COLORS = [
  { r: 255, g: 100, b: 170 },
  { r: 100, g: 140, b: 240 },
  { r: 190, g: 100, b: 210 },
  { r: 255, g: 130, b: 90 },
  { r: 70, g: 210, b: 190 },
  { r: 255, g: 100, b: 130 },
  { r: 150, g: 110, b: 210 },
  { r: 255, g: 150, b: 70 },
  { r: 255, g: 170, b: 100 },
  { r: 120, g: 180, b: 240 },
  { r: 210, g: 120, b: 170 },
  { r: 160, g: 210, b: 120 },
  { r: 255, g: 120, b: 120 },
  { r: 120, g: 160, b: 210 },
  { r: 210, g: 160, b: 120 },
] as const

const adjustSaturation = (
  color: { r: number; g: number; b: number },
  saturationPercent: number,
  alpha: number
): string => {
  const factor = saturationPercent / 100
  const r = Math.round(color.r + (255 - color.r) * (1 - factor))
  const g = Math.round(color.g + (255 - color.g) * (1 - factor))
  const b = Math.round(color.b + (255 - color.b) * (1 - factor))
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const getBaseColorFromId = (id: string) => {
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return BASE_COLORS[hash % BASE_COLORS.length]
}

const getAccentColor = (id: string) => adjustSaturation(getBaseColorFromId(id), 70, 1)
const getAccentBg = (id: string) => adjustSaturation(getBaseColorFromId(id), 40, 0.15)

// ── Sort types ────────────────────────────────────────────────────────────────

type SortKey = 'rank' | 'value' | 'target' | 'performance'
type SortDir = 'asc' | 'desc'

interface IRowData {
  kpiId: string
  title: string
  rank: any
  value: any
  target: any
  performance: any
}

const parseNum = (v: any): number | null => {
  if (v == null) return null
  const n = parseFloat(String(v))
  return isNaN(n) ? null : n
}

const sortRows = (rows: IRowData[], key: SortKey, dir: SortDir): IRowData[] => {
  return [...rows].sort((a, b) => {
    const an = parseNum(a[key])
    const bn = parseNum(b[key])
    if (an == null && bn == null) return 0
    if (an == null) return 1
    if (bn == null) return -1
    return dir === 'asc' ? an - bn : bn - an
  })
}

// ── Column header ─────────────────────────────────────────────────────────────

const GRID_TEMPLATE = 'minmax(0, 1fr) 80px 110px 110px 120px'

interface IColHeaderProps {
  label: string
  sortKey?: SortKey
  currentSort: SortKey
  sortDir: SortDir
  onSort: (key: SortKey) => void
  align?: 'flex-start' | 'center'
}

const ColHeader: React.FC<IColHeaderProps> = ({ label, sortKey, currentSort, sortDir, onSort, align = 'center' }) => {
  const isActive = !!sortKey && currentSort === sortKey
  return (
    <Flex
      alignItems="center"
      justifyContent={align}
      cursor={sortKey ? 'pointer' : 'default'}
      onClick={() => sortKey && onSort(sortKey)}
      // @ts-ignore
      userSelect="none"
      px="10px"
      py="10px"
    >
      <Text
        fontSize="11px"
        fontWeight="700"
        color={isActive ? colors.blackLight : colors.greyMain}
        textTransform="uppercase"
        letterSpacing="0.06em"
      >
        {label}
      </Text>
      {sortKey && (
        <Text as="span" fontSize="10px" ml="4px" color={isActive ? colors.blackLight : colors.greyMain}>
          {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
        </Text>
      )}
    </Flex>
  )
}

// ── Cell value with optional units ────────────────────────────────────────────

const CellValue: React.FC<{ value: any }> = ({ value }) => {
  if (value == null) return <Text fontSize="13px" color={colors.greyMain}>—</Text>
  return (
    <Text fontSize="13px" fontWeight="600" color={colors.blackLight}>
      {value}
      {isValidNumberString(String(value)) && (
        <Text as="span" fontSize="11px" fontWeight="400" color={colors.greyDark}>
          {' '}<LocaleText text="general_units" />
        </Text>
      )}
    </Text>
  )
}

// ── Main modal ────────────────────────────────────────────────────────────────

const AllKpisModal: React.FC<IProps> = ({ isOpen, onClose }) => {
  const [sortKey, setSortKey] = useState<SortKey>('rank')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const { isAllKpisLoading, kpisList, kpisMediaData, rankingReports } = useSelector(
    (state: RootState) => state.ranking
  )
  const { activeGroupID, config: { groups } } = useSelector((state: RootState) => state.config)

  const groupName = activeGroupID ? groups[activeGroupID]?.name : undefined

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const rows = useMemo<IRowData[]>(() => {
    const competitions = kpisList.filter((kpi) => kpi.type === 'competition_kpi')
    return competitions.reduce<IRowData[]>((acc, kpi) => {
      const store = rankingReports[kpi.id]?.stores?.find((s: any) => s.group === activeGroupID)
      if (!store) return acc
      acc.push({
        kpiId: kpi.id,
        title: kpisMediaData[kpi.id]?.title ?? kpi.id,
        rank: store.rank,
        value: store.value,
        target: store.target,
        performance: store.performance,
      })
      return acc
    }, [])
  }, [kpisList, rankingReports, activeGroupID, kpisMediaData])

  const sortedRows = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir])

  const hasData = rows.length > 0

  const headerContent = (
    <Flex flexDir="column" alignItems="center" style={{ rowGap: '2px' }}>
      <Text fontWeight="800" fontSize="18px" color={colors.blackLight}>
        My Performance
      </Text>
      {groupName && (
        <Text fontSize="13px" color={colors.greyDark} fontWeight="400">
          {groupName}
        </Text>
      )}
    </Flex>
  )

  const bodyContent = (
    <Box px="20px" pb="20px" pt="4px">
      {isAllKpisLoading && !hasData ? (
        <Flex justifyContent="center" alignItems="center" h="200px">
          <Spinner size="xl" color={colors.blueMedium} />
        </Flex>
      ) : hasData ? (
        <Box
          bg="white"
          borderRadius="12px"
          boxShadow="0 2px 12px rgba(0,0,0,0.07)"
          overflow="hidden"
        >
          {/* Table header */}
          <Box
            bg="#F7F8FA"
            borderBottom="1px solid #EEF0F2"
            style={{ display: 'grid', gridTemplateColumns: GRID_TEMPLATE }}
          >
            <ColHeader label="Competition" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="flex-start" />
            <ColHeader label="Rank" sortKey="rank" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
            <ColHeader label="Value" sortKey="value" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
            <ColHeader label="Target" sortKey="target" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
            <ColHeader label="Performance" sortKey="performance" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
          </Box>

          {/* Data rows */}
          {sortedRows.map((row, i) => {
            const accentColor = getAccentColor(row.kpiId)
            const accentBg = getAccentBg(row.kpiId)
            const isLast = i === sortedRows.length - 1
            return (
              <Box
                key={row.kpiId}
                borderBottom={isLast ? 'none' : '1px solid #EEF0F2'}
                borderLeft={`4px solid ${accentColor}`}
                bg="white"
                // @ts-ignore
                style={{ display: 'grid', gridTemplateColumns: GRID_TEMPLATE, alignItems: 'center' }}
              >
                {/* Name */}
                <Box px="10px" py="12px">
                  <Text fontSize="13px" fontWeight="600" color={colors.blackLight} isTruncated>
                    <LocaleText text={row.title} />
                  </Text>
                </Box>

                {/* Rank */}
                <Flex justifyContent="center" px="10px">
                  {row.rank != null ? (
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                      bg={accentBg}
                      borderRadius="20px"
                      px="10px"
                      h="26px"
                      minW="44px"
                    >
                      <Text fontWeight="800" fontSize="13px" color={accentColor}>
                        #{row.rank}
                      </Text>
                    </Flex>
                  ) : (
                    <Text fontSize="13px" color={colors.greyMain}>—</Text>
                  )}
                </Flex>

                {/* Value */}
                <Flex justifyContent="center" px="10px">
                  <CellValue value={row.value} />
                </Flex>

                {/* Target */}
                <Flex justifyContent="center" px="10px">
                  <CellValue value={row.target} />
                </Flex>

                {/* Performance */}
                <Flex justifyContent="center" px="10px">
                  <CellValue value={row.performance} />
                </Flex>
              </Box>
            )
          })}
        </Box>
      ) : (
        <Flex justifyContent="center" alignItems="center" h="200px" flexDir="column" style={{ rowGap: '8px' }}>
          <Text fontSize="16px" color={colors.greyDark}>
            No performance data available
          </Text>
        </Flex>
      )}
    </Box>
  )

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      headerContent={headerContent}
      bodyContent={bodyContent}
      maxWidth="900px"
      minWidth="500px"
      heightOffset={180}
      closeOnOverlayClick
      closeOnEsc
    />
  )
}

export default AllKpisModal
